﻿import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

// Global fallback SLA threshold — used when a site has no ISP contract with a defined
// UptimeSlaPercent.  Individual router contracts override this via the router-sla-targets
// endpoint so each site is assessed against its own contractual obligation.
const SLA_THRESHOLD_FALLBACK = Number(import.meta.env.VITE_SLA_THRESHOLD ?? 98);

const MallOutagesChart = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [perMallRows, setPerMallRows] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [outageAlerts, setOutageAlerts] = useState([]);

  // Per-router contractual SLA targets loaded from the ISP contracts.
  // Shape: Map<normalizedSiteName, maxSlaPercent>
  // Both displayName and hostname are registered so either can match a Zabbix host name.
  const [routerSlaMap, setRouterSlaMap] = useState(new Map());

  const SLAIcon = ({ ok }) => ok ? (
    <svg viewBox="0 0 16 16" width="12" height="12" style={{ marginLeft: 4, verticalAlign: 'middle' }} aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="7" fill="#2e7d32" opacity="0.15" />
      <path fill="#2e7d32" d="M6.2 9.6 4.4 7.8l1-1 1.4 1.4L10.6 4.4l1 1z" />
    </svg>
  ) : (
    <svg viewBox="0 0 16 16" width="12" height="12" style={{ marginLeft: 4, verticalAlign: 'middle' }} aria-hidden="true" focusable="false">
      <path d="M8 1.333 14.667 13.333H1.333L8 1.333z" fill="var(--primary-color)" opacity="0.15"/>
      <path d="M8 5.2c.36 0 .65.29.65.65v3.3a.65.65 0 1 1-1.3 0V5.85c0-.36.29-.65.65-.65zm0 6.13a.78.78 0 1 1 0-1.56.78.78 0 0 1 0 1.56z" fill="var(--primary-color)"/>
    </svg>
  );

  const { prevYear, prevMonth, beforePrevYear, beforePrevMonth, prevLabel, beforePrevLabel } = useMemo(() => {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth() + 1;
    let pMonth = month - 1; let pYear = year; if (pMonth === 0) { pMonth = 12; pYear -= 1; }
    let bpMonth = pMonth - 1; let bpYear = pYear; if (bpMonth === 0) { bpMonth = 12; bpYear -= 1; }
    const prevDate = new Date(pYear, pMonth - 1, 1);
    const beforePrevDate = new Date(bpYear, bpMonth - 1, 1);
    const labelOpts = { month: 'long', year: 'numeric' };
    return { prevYear: pYear, prevMonth: pMonth, beforePrevYear: bpYear, beforePrevMonth: bpMonth, prevLabel: prevDate.toLocaleDateString(undefined, labelOpts), beforePrevLabel: beforePrevDate.toLocaleDateString(undefined, labelOpts) };
  }, []);

  // Calculate network reliability health score
  const healthScore = useMemo(() => {
    if (!summary || summary.uptimeLastMonth == null) return null;
    // Network reliability is direct uptime percentage
    return Math.round(summary.uptimeLastMonth);
  }, [summary]);

  // Captured once per outageAlerts change so the duration calculation in the
  // action items table below is pure (React rules disallow calling Date.now()
  // directly during render). Re-evaluates when the alert list changes so the
  // "ongoing" duration stays roughly current after the data refreshes.
  // NOTE: must be declared before any early returns to keep hook order stable.
  const nowTs = useMemo(() => Date.now(), [outageAlerts]);

  const getScoreInterpretation = (score) => {
    if (score == null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 99.5) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 99.0) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 98.0) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 95.0) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Critical', color: '#d32f2f', icon: '✗' };
  };

  // ── Load per-router contractual SLA targets from ISP contracts ────────────────
  // Runs once on mount.  Non-critical — the chart still works if this call fails;
  // it simply falls back to the global SLA_THRESHOLD_FALLBACK for every site.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/ServiceProviders/router-sla-targets');
        if (!res.ok) return;
        const json = await res.json();
        if (!json.success || !Array.isArray(json.data)) return;

        // Build a normalised lookup map so we can match Zabbix host names regardless
        // of how the router was named in CapTrac (display name vs RouterOS identity).
        const slaMap = new Map();
        json.data.forEach(({ displayName, hostname, maxSlaPercent }) => {
          if (typeof maxSlaPercent !== 'number') return;
          if (displayName) slaMap.set(displayName.toLowerCase().trim(), maxSlaPercent);
          if (hostname)    slaMap.set(hostname.toLowerCase().trim(),    maxSlaPercent);
        });
        setRouterSlaMap(slaMap);
      } catch (catchError) {
          Sentry.captureException(catchError);
        // Non-critical — silently fall back to global threshold
      }
    })();
  }, [apiFetch]);

  /**
   * Pre-computed SLA lookup map that includes both exact-match keys (from routerSlaMap)
   * and partial-match keys resolved by iterating the base map once.
   * Built via useMemo so the O(n) partial-match scan only runs when routerSlaMap changes,
   * not on every call to getSlaTarget during the render loop.
   *
   * The returned Map contains: normalizedSiteName → slaPercent for all exact + partial
   * matches that can be inferred from the router names returned by the API.
   */
  const resolvedSlaMap = useMemo(() => {
    // Start with all exact-match entries already in routerSlaMap
    const resolved = new Map(routerSlaMap);

    // For each known router name, also register any site names that are a substring
    // match.  This covers the case where a Zabbix host name is "Sandton City" while
    // the CapTrac display name is "Sandton" (or vice versa).
    // We only add an entry if there is no exact-match entry already so that an exact
    // match always wins over a partial match.
    for (const [mappedName, sla] of routerSlaMap) {
      for (const site of (perMallRows || []).map(r => r.site.toLowerCase().trim())) {
        if (!resolved.has(site) && (site.includes(mappedName) || mappedName.includes(site))) {
          resolved.set(site, sla);
        }
      }
    }
    return resolved;
  }, [routerSlaMap, perMallRows]);

  /**
   * Returns the applicable SLA target percentage for a given site name.
   * Uses the pre-computed resolvedSlaMap (O(1) lookup) to avoid a linear scan
   * in the render loop. Falls back to the global env-var threshold when no
   * contract SLA is found for this site.
   * @param {string} siteName - The site/host name from monitoring events
   * @returns {number} - SLA target percentage (e.g. 99.5)
   */
  const getSlaTarget = (siteName) => {
    const key = (siteName || '').toLowerCase().trim();
    return resolvedSlaMap.has(key) ? resolvedSlaMap.get(key) : SLA_THRESHOLD_FALLBACK;
  };

  useEffect(() => { setLoading(true); fetchTwoPeriods(); }, [prevYear, prevMonth, beforePrevYear, beforePrevMonth]); // eslint-disable-line

  const toLocalDate = (d) => (
    d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
  );

  const sumMergedMs = (intervals) => {
    if (!intervals || intervals.length === 0) return 0;
    const sorted = intervals.slice().sort((a, b) => a[0] - b[0]);
    let total = 0; let [curStart, curEnd] = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      const [s, e] = sorted[i];
      if (s <= curEnd) curEnd = Math.max(curEnd, e); else { total += Math.max(0, curEnd - curStart); curStart = s; curEnd = e; }
    }
    total += Math.max(0, curEnd - curStart);
    return total;
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const order = [1,2,3,4,5,6,0];
  const colorFor = useMemo(() => {
    const parseColor = (c) => {
      if (!c) return { r:0,g:0,b:0 }; let h=c.trim();
      if (h.startsWith('#')) { if (h.length===4) h = '#'+[...h.slice(1)].map(x=>x+x).join(''); const num=parseInt(h.slice(1),16); return { r:(num>>16)&255,g:(num>>8)&255,b:num&255 }; }
      const el = document.createElement('div'); el.style.color = c; document.body.appendChild(el); const cs = getComputedStyle(el).color; document.body.removeChild(el); const m = cs.match(/(\d+),(\d+),(\d+)/); return m?{r:+m[1],g:+m[2],b:+m[3]}:{r:0,g:0,b:0};
    };
    const root = getComputedStyle(document.documentElement);
    const minRgb = parseColor(root.getPropertyValue('--heatmap-min-color')?.trim() || '#57565B');
    const maxRgb = parseColor(root.getPropertyValue('--heatmap-max-color')?.trim() || '#F63040');
    return (v, max) => {
      if (max <= 0 || v <= 0) return `rgb(${minRgb.r},${minRgb.g},${minRgb.b})`;
      const t = Math.min(1, v / max);
      const r = Math.round(minRgb.r + (maxRgb.r - minRgb.r) * t);
      const g = Math.round(minRgb.g + (maxRgb.g - minRgb.g) * t);
      const b = Math.round(minRgb.b + (maxRgb.b - minRgb.b) * t);
      return `rgb(${r},${g},${b})`;
    };
  }, []);

  async function fetchPeriodOutages(year, month) {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0, 23, 59, 59);
    const startStr = toLocalDate(startDate);
    const endStr = toLocalDate(endDate);
    const response = await apiFetch(`/api/RouterConfig/mall-outage-events?startDate=${startStr}&endDate=${endStr}`);
    const data = await response.json();
    const daysInMonth = endDate.getDate();
    const dailyCounts = Array(daysInMonth).fill(0);
    const siteIntervals = {}; const siteOutageCounts = {}; const siteMonitoredDays = {}; const heat = Array.from({ length: 7 }, () => Array(24).fill(0));
    let allSitesSet = new Set();
    if (data?.events) {
      data.events.forEach(p => {
        const ts = p.clock ? parseInt(p.clock) : null; const siteNames = Array.isArray(p.hosts)? p.hosts.map(h=>h.name).filter(Boolean):[]; siteNames.forEach(s=>allSitesSet.add(s));
        if (ts) {
          const d = new Date(ts * 1000);
            if (d.getMonth() + 1 === month) {
              const dayIdx = d.getDate() - 1; dailyCounts[dayIdx] = (dailyCounts[dayIdx] || 0) + 1;
              if (p.duration_seconds) {
                const durationMs = p.duration_seconds * 1000; const startMs = d.getTime(); const endMs = startMs + durationMs;
                siteNames.forEach(site => { if (!siteIntervals[site]) siteIntervals[site] = []; siteIntervals[site].push([startMs,endMs]); if (!siteOutageCounts[site]) siteOutageCounts[site]=0; siteOutageCounts[site]++; if (!siteMonitoredDays[site]) siteMonitoredDays[site]=new Set(); siteMonitoredDays[site].add(dayIdx); });
                let remaining = durationMs; let cursor = new Date(d);
                while (remaining > 0) {
                  const boundary = new Date(cursor); boundary.setMinutes(0,0,0); boundary.setHours(boundary.getHours()+1);
                  const sliceMs = Math.min(remaining, boundary - cursor); const minutes = Math.ceil(sliceMs/60000);
                  const dow = cursor.getDay(); const hr = cursor.getHours(); if (minutes>0) heat[dow][hr]+=minutes;
                  cursor = new Date(cursor.getTime()+sliceMs); remaining -= sliceMs;
                }
              }
            }
        }
      });
    }
    Array.from(allSitesSet).forEach(site => { if (!siteMonitoredDays[site]) siteMonitoredDays[site] = new Set(Array.from({ length: daysInMonth }, (_, i) => i)); });
    const siteOutageHours = {}; Object.keys(siteIntervals).forEach(site => { siteOutageHours[site] = sumMergedMs(siteIntervals[site]) / 3600000; });
    return { daily: dailyCounts.map((count,i)=>({ day:i+1, outages:count })), daysInMonth, siteOutageHours, siteOutageCounts, siteMonitoredDays, heatmap: heat };
  }

  async function fetchTwoPeriods() {
    try {
      const [previous, beforePrevious] = await Promise.all([
        fetchPeriodOutages(prevYear, prevMonth),
        fetchPeriodOutages(beforePrevYear, beforePrevMonth)
      ]);
      const maxDays = Math.max(previous.daily.length, beforePrevious.daily.length);
      const merged = Array.from({ length: maxDays }, (_, idx) => ({ day: idx + 1, previous: previous.daily[idx]?.outages || 0, beforePrevious: beforePrevious.daily[idx]?.outages || 0 }));
      setChartData(merged);

      function meanUptime(siteOutageHours, days) {
        const entries = Object.values(siteOutageHours); if (!entries.length) return 100; const monHours = days*24; return entries.reduce((a,b)=> a + ((monHours - b) / monHours * 100),0)/entries.length;
      }
      const uptimePrev = meanUptime(previous.siteOutageHours, previous.daysInMonth);
      const uptimePrior = meanUptime(beforePrevious.siteOutageHours, beforePrevious.daysInMonth);
      const hoursPrev = Object.values(previous.siteOutageHours).reduce((a,b)=>a+b,0);
      const hoursPrior = Object.values(beforePrevious.siteOutageHours).reduce((a,b)=>a+b,0);
      const countPrev = Object.values(previous.siteOutageCounts).reduce((a,b)=>a+b,0);
      const countPrior = Object.values(beforePrevious.siteOutageCounts).reduce((a,b)=>a+b,0);

      setSummary({ prevLabel, beforePrevLabel, uptimeLastMonth: uptimePrev, uptimeMonthBefore: uptimePrior, uptimeChange: uptimePrev - uptimePrior, totalHoursLastMonth: hoursPrev, totalHoursMonthBefore: hoursPrior, hoursChange: hoursPrior===0?0:((hoursPrev-hoursPrior)/hoursPrior)*100, totalOutagesLastMonth: countPrev, totalOutagesMonthBefore: countPrior, outagesChange: countPrior===0?0:((countPrev-countPrior)/countPrior)*100 });

      const hm = previous.heatmap.map(r=> r.map(m => m>=1?m:0));
      const max = hm.reduce((mx,row)=> Math.max(mx, ...row), 0);
      setHeatmap({ matrix: hm, max });

      const rows = Object.keys(previous.siteOutageHours).map(site => {
        const hours = previous.siteOutageHours[site] || 0;
        const count = previous.siteOutageCounts?.[site] || 0;
        const monHours = previous.daysInMonth * 24;
        // Actual achieved uptime percentage for this site this month.
        // slaTarget is intentionally NOT stored here — it is looked up at render time
        // from routerSlaMap so that the comparison auto-updates once the ISP contract
        // data has loaded (which happens in a separate async effect).
        const sla = monHours > 0 ? ((monHours - hours) / monHours) * 100 : 100;
        return { site, count, hours, sla };
      }).filter(r => r.hours >= 1).sort((a, b) => b.hours - a.hours);
      setPerMallRows(rows);
    }
    catch (e) {
        Sentry.captureException(e);
        setError(e.message); setSummary(null); setChartData([]); setPerMallRows([]); setHeatmap(null); setOutageAlerts([]);
    }
    finally { setLoading(false); }
  }

  // Fetch router monitoring Availability-type action items for the previous calendar
  // month.  Router monitoring is only ONE part of the system that creates action items
  // (others include SIPA, non-compliance checks, project tasks, etc.).  We must filter
  // precisely so we only count genuine network outage items:
  //   • source === "RouterMonitoring"  — created by the automated alert service
  //   • title includes "Availability"  — distinguishes full outage items from secondary
  //     PacketLossThreshold items that are appended to the same action item but would
  //     appear as separate records if they fired independently.
  // The main uptime/downtime/outage counts shown in the metrics above come from InfluxDB
  // aggregations (mall-outage-events), which remain the authoritative source for the
  // network reliability figures.  This table is a supplementary view linking those outages
  // back to the action items that were raised so engineers can drill in quickly.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/ActionItems/all');
        if (!res.ok) return;
        const data = await res.json();
        const items = Array.isArray(data) ? data : (data?.data ?? data?.items ?? []);

        // Determine the UTC boundaries of the previous calendar month so the filter
        // aligns exactly with the outage metrics period shown in the chart above.
        const now = new Date();
        const prevMonthStart = new Date(Date.UTC(
          now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear(),
          now.getMonth() === 0 ? 11 : now.getMonth() - 1,
          1
        ));
        const prevMonthEnd = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));

        const monthAlerts = items.filter(item => {
          // Must be a RouterMonitoring action item — excludes all other sources
          const source = item.source || item.Source || '';
          if (source !== 'RouterMonitoring') return false;

          // Must be an Availability alert — PacketLossThreshold alerts that fire
          // independently are NOT the same as a full connectivity outage.
          const title = item.title || item.Title || '';
          if (!title.includes('Availability')) return false;

          // Must have been created within the previous calendar month
          const created = new Date(item.createdDate || item.CreatedDate || 0);
          return created >= prevMonthStart && created < prevMonthEnd;
        });

        // Sort newest-first and cap at 15 rows for readability
        monthAlerts.sort((a, b) =>
          new Date(b.createdDate || b.CreatedDate || 0) -
          new Date(a.createdDate || a.CreatedDate || 0)
        );
        setOutageAlerts(monthAlerts.slice(0, 15));
      }
      catch (catchError) {
          Sentry.captureException(catchError);
          /* non-critical — section is simply hidden when empty */
      }
    })();
  }, [apiFetch, prevYear, prevMonth]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const row = chartData[label - 1] || {}; return (
        <div style={{ background:'#fff', border:'1px solid #ccc', padding:8, borderRadius:4, fontSize:11 }}>
          <div><strong>Day {label}</strong></div>
          <div>{beforePrevLabel}: {row.beforePrevious}</div>
          <div>{prevLabel}: {row.previous}</div>
        </div>
      );
    }
    return null;
  };

  const InlineIndicator = ({ value, isUptime }) => { if (value === 0) return null; const isUp = value > 0; const color = isUptime ? (isUp ? 'green' : 'red') : (isUp ? 'red' : 'green'); const arrow = isUp ? '↑' : '↓'; return <span style={{ color, fontWeight:600, marginLeft:4 }}>{arrow}{Math.abs(value).toFixed(1)}{isUptime ? ' pts' : '%'}</span>; };

  if (loading) return <HexagonLoader />;
  if (error) return <div style={{ color:'red' }}>{error}</div>;

  const scoreInterp = getScoreInterpretation(healthScore);
  const uptimePct = summary?.uptimeLastMonth ?? 0;
  const totalHours = summary?.totalHoursLastMonth ?? 0;
  // Outage Incidents card: prefer the InfluxDB-derived count, but fall back to the
  // count of router monitoring Availability action items raised in the period when
  // the InfluxDB aggregation reports zero. Short outages (<30m) often don't tip the
  // hourly aggregation past the 50% unreachable threshold but DO trigger Action Items
  // via RouterAlertService — without this fallback the card reads "0 incidents" while
  // the table below clearly lists multiple alerts, which is confusing for executives.
  const influxOutages = summary?.totalOutagesLastMonth ?? 0;
  const totalOutages = influxOutages > 0 ? influxOutages : outageAlerts.length;
  const outagesFromActionItems = influxOutages === 0 && outageAlerts.length > 0;

  // Each row is compared against its own per-site contractual SLA target (from ISP
  // contracts via the router-sla-targets endpoint) rather than a single global value.
  // getSlaTarget reads from routerSlaMap state, so this re-computes automatically
  // once the SLA targets have loaded.
  const sitesUnderSLA = perMallRows.filter(r => r.sla < getSlaTarget(r.site)).length;

  const uptimeChange = summary?.uptimeChange ?? 0;

  const llmProxyText = [
    `Network Reliability Score: ${healthScore}/100`,
    `Uptime: ${uptimePct.toFixed(2)}%`,
    `Total downtime: ${totalHours.toFixed(2)} hours`,
    `Outage incidents: ${totalOutages}`
  ].join('\n');

  return (
    <div className="dashboard-card group-wrapper mall-outages-chart">
    <div data-llm-proxy style={{ display:'none' }}>{llmProxyText}</div>
    <h2 style={{ marginTop:0, marginBottom: 4 }}>Network Reliability & Mall Connectivity</h2>
    <div className="pdf-exclude" style={{ fontSize: 11, color: '#6c757d', marginBottom: 12, fontStyle: 'italic' }}>
      Outage detection: ICMP availability sampled per minute, aggregated into 5-minute windows.
      A window counts as an outage when the router was unreachable for &gt;50% of the window.
      Consecutive down-windows are merged into a single incident; outages shorter than 60 seconds
      are filtered as monitoring noise.
    </div>

      {/* Executive Summary Banner */}
      <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
        <div className="exec-banner-layout">
          <div className="exec-banner-score">
            <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
              {healthScore != null ? `${healthScore.toFixed(1)}%` : '–'}
            </div>
            <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
            <div className="exec-banner-sublabel">Network Uptime ({summary?.prevLabel})</div>
          </div>
          <div className="exec-banner-body">
            <div className="exec-banner-text">
              <strong>What this means:</strong> Your mall network achieved <strong>{uptimePct.toFixed(2)}% uptime</strong> in {summary?.prevLabel} with <strong>{totalHours.toFixed(1)} hours of downtime</strong> across {totalOutages} outage incidents.
              {uptimePct >= 99.5 && ' Network reliability is excellent.'}
              {uptimePct >= 99 && uptimePct < 99.5 && ' Network reliability is good with minor issues.'}
              {uptimePct < 99 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> Frequent outages are impacting business operations and user productivity.</span>}
              {sitesUnderSLA > 0 && <span style={{ color: '#ff5722', fontWeight: 600 }}> {sitesUnderSLA} site{sitesUnderSLA > 1 ? 's' : ''} below their contractual SLA target - investigate connectivity issues.</span>}
              {uptimeChange !== 0 && (
                <span style={{ color: uptimeChange > 0 ? 'green' : '#d32f2f', fontWeight: 600 }}>
                  {' '}Reliability {uptimeChange > 0 ? 'improved' : 'degraded'} by {Math.abs(uptimeChange).toFixed(1)} points vs prior month.
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-row">
        <div className="dashboard-card metric-card" title="Percentage of time network was accessible across all sites">
          <div className="metric-label">Network Uptime</div>
          <div className="metric-value--md" style={{ fontWeight:700, color: uptimePct >= 99.5 ? '#2e7d32' : uptimePct >= 99 ? '#7cb342' : uptimePct >= 98 ? '#ffa000' : '#d32f2f' }}>
            {uptimePct.toFixed(2)}%
          </div>
          {uptimeChange !== 0 && (
            <div className="metric-delta" style={{ color: uptimeChange > 0 ? 'green' : '#d32f2f' }}>
              {uptimeChange > 0 ? '↑' : '↓'} {Math.abs(uptimeChange).toFixed(2)} pts
            </div>
          )}
        </div>
        <div className="dashboard-card metric-card" title="Total hours of network unavailability">
          <div className="metric-label">Total Downtime</div>
          <div className="metric-value--md" style={{ fontWeight:700, color: totalHours < 10 ? '#2e7d32' : totalHours < 50 ? '#ffa000' : '#d32f2f' }}>
            {totalHours.toFixed(1)}h
          </div>
          {summary?.hoursChange !== 0 && (
            <div className="metric-delta" style={{ color: (summary?.hoursChange || 0) < 0 ? 'green' : '#d32f2f' }}>
              {(summary?.hoursChange || 0) >= 0 ? '↑' : '↓'} {Math.abs(summary?.hoursChange || 0).toFixed(1)}%
            </div>
          )}
        </div>
        <div className="dashboard-card metric-card" title={outagesFromActionItems ? 'No InfluxDB outage events for the period — falling back to Router Monitoring action items raised in the period' : 'Number of network outage incidents (≥60 seconds, after 5-min-window aggregation and consecutive-window merging)'}>
          <div className="metric-label">Outage Incidents</div>
          <div className="metric-value--md" style={{ fontWeight:700, color: totalOutages < 10 ? '#2e7d32' : totalOutages < 25 ? '#ffa000' : '#d32f2f' }}>
            {totalOutages}
          </div>
          {outagesFromActionItems && (
            <div className="metric-subtitle" style={{ fontSize: 10, color: '#888', marginTop: 2 }}>from action items</div>
          )}
          {!outagesFromActionItems && summary?.outagesChange !== 0 && (
            <div className="metric-delta" style={{ color: (summary?.outagesChange || 0) < 0 ? 'green' : '#d32f2f' }}>
              {(summary?.outagesChange || 0) >= 0 ? '↑' : '↓'} {Math.abs(summary?.outagesChange || 0).toFixed(1)}%
            </div>
          )}
        </div>
        <div className="dashboard-card metric-card" title="Sites whose actual uptime is below their contractual ISP SLA target">
          <div className="metric-label">Sites Below SLA</div>
          <div className="metric-value--md" style={{ fontWeight:700, color: sitesUnderSLA === 0 ? '#2e7d32' : sitesUnderSLA < 3 ? '#ffa000' : '#d32f2f' }}>
            {sitesUnderSLA}
          </div>
          <div className="metric-subtitle" style={{ marginTop: 4 }}>
            {sitesUnderSLA === 0 ? 'All sites meeting SLA' : 'Per-site ISP contract targets'}
          </div>
        </div>
      </div>

      {/* Comparison Table and Chart */}
      <div className="dashboard-grid compact">
        <div className="dashboard-card chart-card chart-card--wide">
          <h4 className="chart-title">Monthly Comparison</h4>
          <div className="chart-subtitle">Month-over-month network performance</div>
          {summary && (
            <table className="data-table">
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ textAlign:'left', width: '100%', padding: '6px' }}>Metric</th>
                  <th style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{beforePrevLabel}</th>
                  <th style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{prevLabel}</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ textAlign:'left', padding: '6px', fontWeight: 600 }}>Downtime (Hours)</td>
                  <td style={{ padding: '6px', textAlign: 'right' }}>{summary.totalHoursMonthBefore.toFixed(2)}</td>
                  <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600 }}>{summary.totalHoursLastMonth.toFixed(2)}<InlineIndicator value={summary.hoursChange} /></td>
                </tr>
                <tr style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ textAlign:'left', padding: '6px', fontWeight: 600 }}>Uptime Percentage</td>
                  <td style={{ padding: '6px', textAlign: 'right' }}>{summary.uptimeMonthBefore.toFixed(2)}%</td>
                  <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600 }}>{summary.uptimeLastMonth.toFixed(2)}%<InlineIndicator value={summary.uptimeChange} isUptime /></td>
                </tr>
                <tr style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ textAlign:'left', padding: '6px', fontWeight: 600 }}>Outage Count</td>
                  <td style={{ padding: '6px', textAlign: 'right' }}>{summary.totalOutagesMonthBefore}</td>
                  <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600 }}>{summary.totalOutagesLastMonth}<InlineIndicator value={summary.outagesChange} /></td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Daily Outage Frequency</h4>
          <div className="chart-subtitle">Incident count per day - identify patterns</div>
          <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={180} minHeight={180}>
            <LineChart data={chartData} margin={{ top:4, right:8, left:0, bottom:4 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" label={{ value: 'Day of Month', position: 'insideBottom', offset: -5 }} />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="beforePrevious" name={beforePrevLabel} stroke="var(--secondary-color)" strokeDasharray="6 3" strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} />
              <Line type="monotone" dataKey="previous" name={prevLabel} stroke="var(--primary-color)" strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Site-Specific SLA Table */}
      {perMallRows.length > 0 && (
        <div className="dashboard-card">
          <h4 style={{ margin:'0 0 8px 0', fontSize:14 }}>Site-Specific Network Performance ({summary?.prevLabel})</h4>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>
            Sites with outages ≥1 hour — sorted by total downtime.
            SLA Target column shows the contractual uptime obligation from the site's ISP contract (fallback: {SLA_THRESHOLD_FALLBACK}%).
          </div>
          <table className="data-table">
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ textAlign:'left', padding: '6px', width: '100%' }}>Site/Mall</th>
                <th style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>Outage Count</th>
                <th style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>Downtime (Hours)</th>
                {/* Achieved uptime this month */}
                <th style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>Achieved %</th>
                {/* Per-site contractual SLA target */}
                <th style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>SLA Target</th>
              </tr>
            </thead>
            <tbody>
              {perMallRows.map(r => {
                // Look up this site's contractual SLA target at render time so
                // the value is always fresh after routerSlaMap has loaded.
                const slaTarget = getSlaTarget(r.site);
                // Compare achieved uptime against this site's contractual target
                const warn = r.sla < slaTarget;
                // Indicate when a site's target comes from the ISP contract vs fallback
                const hasContractSla = slaTarget !== SLA_THRESHOLD_FALLBACK;
                return (
                  <tr key={r.site} style={{ borderTop: '1px solid #eee', ...(warn ? { background:'rgba(246,48,64,0.06)' } : {}) }}>
                    <td style={{ textAlign:'left', padding: '6px', fontWeight: 600 }}>{r.site}</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>{r.count}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: r.hours > 10 ? '#d32f2f' : r.hours > 5 ? '#ffa000' : '#666' }}>
                      {r.hours.toFixed(2)}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight:600, whiteSpace: 'nowrap', color: warn ? 'var(--primary-color)' : '#2e7d32' }}>
                      {r.sla.toFixed(2)}% <SLAIcon ok={!warn} />
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap', color: '#555', fontSize: 11 }}
                        title={hasContractSla ? 'ISP contract SLA target' : `Global fallback (${SLA_THRESHOLD_FALLBACK}% — no ISP contract defined)`}>
                      {slaTarget.toFixed(1)}%
                      {hasContractSla && (
                        <span style={{ marginLeft: 4, fontSize: 9, color: '#1976d2', fontWeight: 700 }} title="From ISP contract">★</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize:10, color:'#666', marginTop:6 }}>
            ★ SLA Target sourced from the router's ISP contract. Sites without a defined contract use the fallback threshold ({SLA_THRESHOLD_FALLBACK}%).
            Sites highlighted in red are below their contractual SLA target.
          </div>
        </div>
      )}

      {/* Outage Heatmap */}
      {heatmap && (
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Outage Pattern Heatmap ({summary?.prevLabel})</h4>
          <div className="chart-subtitle">When outages occur (day of week × hour of day) - helps identify patterns and root causes</div>
          <div style={{ overflowX:'auto' }}>
            <table className="heatmap-table">
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ textAlign:'left', padding: '4px 6px', fontSize: 10 }}>Day/Hour</th>
                  {Array.from({ length:24 }, (_,h)=>(<th key={h} style={{ fontSize:9, padding: '4px 2px' }}>{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {order.map(dow => (
                  <tr key={dow}>
                    <td style={{ textAlign:'left', fontWeight:600, padding: '2px 6px', fontSize: 11 }}>{dayNames[dow]}</td>
                    {Array.from({ length:24 }, (_,h) => {
                      const minutes = heatmap.matrix[dow][h] || 0; 
                      const bg = colorFor(minutes, heatmap.max); 
                      return <td key={h} title={`${dayNames[dow]} ${h}:00 = ${minutes}m downtime`} style={{ width:16, height:16, background:bg, borderRadius:2, cursor: 'help' }} />; 
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize:10, color:'#666', marginTop:6 }}>Darker cells = more downtime in that hour/day. Cells {'<'}1 minute hidden. Use to identify peak outage times (e.g., business hours = ISP issue, overnight = maintenance).</div>
        </div>
      )}

      {/* Recent Outage Alerts from Router Monitoring */}
      {outageAlerts.length > 0 && (
        <div className="dashboard-card">
          <h4 style={{ margin:'0 0 8px 0', fontSize:14 }}>Network Outage Action Items — {summary?.prevLabel}</h4>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>
            Router monitoring <strong>Availability</strong> action items created during {summary?.prevLabel}.
            Uptime figures above are derived from live InfluxDB telemetry, not from this list.
          </div>
          <table className="data-table">
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ textAlign:'left', padding: '6px', width: '40%' }}>Alert</th>
                <th style={{ padding: '6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Priority</th>
                <th style={{ padding: '6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Status</th>
                <th style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>Created</th>
                <th style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {outageAlerts.slice(0, 15).map(alert => {
                const created = new Date(alert.createdDate || alert.CreatedDate);
                const resolved = alert.actualCompletionDate || alert.ActualCompletionDate;
                const status = alert.status || alert.Status || 'Open';
                // Terminal statuses indicate the outage is resolved even if the
                // ActualCompletionDate field is empty (legacy items closed before the
                // resolution timestamp was being persisted by RouterAlertService).
                const terminalStatuses = ['Solved', 'Closed', 'Resolved', 'Cancelled', 'Done'];
                const isTerminal = terminalStatuses.includes(status);
                // Choose the best available end timestamp:
                //  1. ActualCompletionDate when set (preferred — matches the Solved StatusChange log)
                //  2. The action item's CreatedDate when terminal but no completion date is recorded
                //     (avoids showing "ongoing" for an item that is clearly closed).
                const endTs = resolved
                  ? new Date(resolved).getTime()
                  : (isTerminal ? created.getTime() : nowTs);
                const duration = Math.max(0, (endTs - created.getTime()) / 60000);
                const priority = alert.priority || alert.Priority || 'Medium';
                const title = alert.title || alert.Title || '';
                const alertId = alert.id || alert.ID;
                const priorityColor = priority === 'Critical' ? '#d32f2f' : priority === 'High' ? '#ff5722' : priority === 'Medium' ? '#ffa000' : '#666';
                const statusColor = isTerminal ? '#2e7d32' : status === 'Open' ? '#d32f2f' : '#ffa000';
                return (
                  <tr key={alertId} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ textAlign:'left', padding: '6px', fontSize: 12 }}>
                      {/* Deep-link to the action item so engineers can open it directly */}
                      <a
                        href={`/action-items/${alertId}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: '#007bff', textDecoration: 'none', fontWeight: 500 }}
                        onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}
                      >
                        {title}
                      </a>
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3, background: `${priorityColor}15`, color: priorityColor }}>{priority}</span>
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3, background: `${statusColor}15`, color: statusColor }}>{status}</span>
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', fontSize: 11, color: '#666' }}>{created.toLocaleDateString()}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: duration > 120 ? '#d32f2f' : '#666' }}>
                      {duration < 1 ? '<1m' : duration < 60 ? `${Math.floor(duration)}m` : duration < 1440 ? `${Math.floor(duration / 60)}h ${Math.floor(duration % 60)}m` : `${Math.floor(duration / 1440)}d`}
                      {!resolved && !isTerminal && <span style={{ fontSize: 9, color: '#d32f2f', marginLeft: 4 }}>ongoing</span>}
                      {!resolved && isTerminal && <span style={{ fontSize: 9, color: '#999', marginLeft: 4 }} title="Item closed without an explicit resolution timestamp">≈</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Explanation Section */}
      <div className="explanation-box">
        <strong>Understanding Network Reliability Metrics:</strong>
        <ul>
          <li><strong>Network Reliability Score:</strong> Direct uptime percentage. Target is {'>'}99% for business operations. {'<'}99% indicates frequent disruptions.</li>
          <li><strong>Uptime Percentage:</strong> Time network was accessible. 99.5% = 3.6h downtime/month, 99% = 7.2h/month, 98% = 14.4h/month.</li>
          <li><strong>SLA (Service Level Agreement):</strong> Per-site contractual uptime obligation sourced from ISP contracts defined on each router. Sites without a contract use the global fallback ({SLA_THRESHOLD_FALLBACK}%). Sites below their contractual target require ISP review or infrastructure upgrades.</li>
          <li><strong>Outage Patterns:</strong> Heatmap reveals root causes: Business hours = ISP/external, Overnight = maintenance, Weekends = power/environmental.</li>
          <li><strong>Site-Specific Issues:</strong> Some malls may have chronic connectivity problems requiring dedicated investigation (aging equipment, poor ISP service, environmental factors).</li>
          <li><strong>Business Impact:</strong> Each outage hour affects: POS systems, security cameras, staff communication, property management systems. Multiply downtime by affected mall count for true impact.</li>
        </ul>
        {sitesUnderSLA > 0 && (
          <div className="action-callout">
            <strong>Action Required:</strong> {sitesUnderSLA} site{sitesUnderSLA > 1 ? 's are' : ' is'} below their contractual ISP SLA target. Review with ISP for potential service credits. Consider backup connectivity (cellular failover, secondary ISP) for chronic problem sites. Document business impact for budget justification.
          </div>
        )}
      </div>
    </div>
  );
};

export default MallOutagesChart;
